import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { type InquiryParticipant } from './useChat';
import { useConversation } from './useConversation';
import useProgressiveLoading from './useProgressiveLoading';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  formatInquiryResponse,
  FormatResponseError,
  FORMAT_RESPONSE_DEFAULT_ERROR,
} from '@/services/formatInquiryResponse';
import { getInquiryV2QueryOptions } from '@/services/getInquiryV2';
import { segmentClient } from '@/util/analytics';
import {
  buildFormatMessageFromText,
  getLastExpertResponse,
  hasExpertResponses,
} from '@/util/buildFormatMessage';
import { wrapFormattedResponse } from '@/util/formatResponseWrapper';

type UseFormatResponseToolOptions = {
  recipient: InquiryParticipant;
};

type FormatFormData = {
  expertResponse: string;
  additionalInstructions: string;
};

function formatResponseToolErrorMessage(
  mutationError: unknown,
  isErrorFlag: boolean
): string | null {
  if (mutationError instanceof FormatResponseError) {
    return mutationError.message;
  }

  if (isErrorFlag) {
    return FORMAT_RESPONSE_DEFAULT_ERROR;
  }

  return null;
}

export function useFormatResponseTool({ recipient }: UseFormatResponseToolOptions) {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const [selectedInquiryUserId, setSelectedInquiryUserId] = useState<number | null>(null);

  const [originalSubmittedResponse, setOriginalSubmittedResponse] = useState<string | null>(null);

  const inquiryAuthorLastName = recipient.lastName ?? '';

  const messages = useConversation(selectedConversationId ?? '');

  const formatStartTimeRef = useRef<number | null>(null);

  const { mutate, isPending, isError, error, data, reset } = useMutation({
    mutationKey: ['formatResponse', selectedConversationId],
    mutationFn: (text: string) => formatInquiryResponse(text),
    onSuccess: (result) => {
      const processingTimeMs = formatStartTimeRef.current
        ? Date.now() - formatStartTimeRef.current
        : 0;

      segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_COMPLETED, {
        response_length: result.formattedResponse.length,
        processing_time_ms: processingTimeMs,
      });

      formatStartTimeRef.current = null;
    },
    onError: (err) => {
      const formatError = err instanceof FormatResponseError ? err : null;

      segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_FAILED, {
        error: formatError?.message ?? 'Unknown error',
        error_code: formatError?.statusCode ?? 500,
      });

      formatStartTimeRef.current = null;
    },
  });

  const { message: progressMessage, elapsedMs } = useProgressiveLoading(isPending);

  const { data: inquiryData } = useQuery({
    ...getInquiryV2QueryOptions({ conversationId: selectedConversationId ?? '' }),
    enabled: !!selectedConversationId,
  });

  const expertLastName = inquiryData?.recipient?.lastName ?? '';

  const hasExpertResponse =
    selectedInquiryUserId !== null && hasExpertResponses(messages, selectedInquiryUserId);

  const lastExpertResponseMessages =
    selectedInquiryUserId !== null ? getLastExpertResponse(messages, selectedInquiryUserId) : [];

  const initialExpertResponse =
    lastExpertResponseMessages.length > 0
      ? [...lastExpertResponseMessages]
          .reverse()
          .map((msg) => msg.text)
          .join('\n\n')
      : null;

  const handleInquirySelect = useCallback(
    (newConversationId: string, newInquiryUserId: number) => {
      if (newConversationId === selectedConversationId) {
        return;
      }

      setSelectedConversationId(newConversationId);

      setSelectedInquiryUserId(newInquiryUserId);

      if (selectedConversationId !== null) {
        reset();
      }
    },
    [selectedConversationId, reset]
  );

  const handleFormat = useCallback(
    (formData: FormatFormData) => {
      setOriginalSubmittedResponse(formData.expertResponse);

      segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_TRIGGERED, {
        source: 'support_tools_panel',
      });

      formatStartTimeRef.current = Date.now();

      const compositeText = buildFormatMessageFromText({
        expertResponseText: formData.expertResponse,
        additionalInstructions: formData.additionalInstructions,
        messages,
        inquiryUserId: selectedInquiryUserId ?? undefined,
      });

      mutate(compositeText);
    },
    [mutate, messages, selectedInquiryUserId]
  );

  const handleReset = useCallback(() => {
    setOriginalSubmittedResponse(null);

    reset();
  }, [reset]);

  const errorMessage = formatResponseToolErrorMessage(error, isError);

  const wrapText = useCallback(
    (text: string) => {
      return wrapFormattedResponse({
        aiFormattedResponse: text,
        recipientLastName: inquiryAuthorLastName,
        expertLastName,
      });
    },
    [inquiryAuthorLastName, expertLastName]
  );

  return {
    // State
    selectedConversationId,

    // Derived state
    initialExpertResponse,
    hasExpertResponse,
    formattedResponse: data?.formattedResponse ?? null,
    originalSubmittedResponse,

    // Loading/error states
    isPending,
    isError,
    error,
    errorMessage,

    // Progressive loading
    progressMessage,
    elapsedMs,

    // Actions
    handleInquirySelect,
    handleFormat,
    handleReset,
    wrapText,
  };
}
